// strona główna

import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import { useTranslation, Trans } from 'react-i18next';
import each from 'lodash/each';

import { reduxActions, useDispatch } from 'store';
import { useGetContract, usePostContractAgreementToggle, useGetLayoutUrlResolver } from 'api';
import { getTranslationByLanguage } from 'api/helpers';

import { HtmlBlock } from 'components/containers/HtmlBlock';
import { generateHomePageScheme } from 'api/helpers';

import { Container, Modal, Button, Checkbox } from 'components/controls';

import styles from 'theme/pages/Home/Home.module.scss';

const Home = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  // Aktualny język aplikacji (np. "pl", "en")
  const currentLanguage = i18n.language;

  // wybrane zgody
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);

  const [isModalVisible, setIsModalVisible] = useState(true);

  useEffect(() => {
    dispatch(reduxActions.setSearchFilterCategory({ id: null, name: '', url_link: null }));
  }, []);

  // Pobieranie danych SEO dla strony głównej
  const { data: urlResolverData } = useGetLayoutUrlResolver(
    { urlKey: '/' },
    {
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const seoData = useMemo(() => {
    if (!urlResolverData) return null;

    return {
      title: getTranslationByLanguage(urlResolverData.meta_title, currentLanguage),
      description: getTranslationByLanguage(urlResolverData.meta_description, currentLanguage),
      keywords: getTranslationByLanguage(urlResolverData.meta_keywords, currentLanguage),
      h1: getTranslationByLanguage(urlResolverData.h1, currentLanguage),
      ogTitle: getTranslationByLanguage(urlResolverData.open_graph_title, currentLanguage),
      ogDescription: getTranslationByLanguage(
        urlResolverData.open_graph_description,
        currentLanguage
      ),
      ogImage: urlResolverData.open_graph_image
    };
  }, [urlResolverData, currentLanguage]);

  const { data: contractData } = useGetContract({
    page: 1,
    limit: 999,
    context: 'HOME_PAGE',
    currentContext: 'HOME_PAGE',
    onlyMustAccept: true
  });

  // zmiana zgód
  const { mutate: updateContractAgreement } = usePostContractAgreementToggle({
    onSuccess: () => {
      setIsModalVisible(false);
    }
  });

  // ustawienie aktywnych zgód
  useEffect(() => {
    if (contractData) {
      const contractIds: number[] = [];
      each(contractData?.items, (contract) => {
        if (contract.has_active_agree) {
          contractIds.push(contract.id);
        }
      });

      setSelectedContractIds(contractIds);
    }
  }, [contractData]);

  // Generowanie JSON-LD schema dla strony głównej
  const homePageScheme = useMemo(() => {
    return generateHomePageScheme(seoData, window.location.href);
  }, [seoData]);

  const handleContractClick = (contractId: number) => {
    if (selectedContractIds.some((item) => item === contractId)) {
      setSelectedContractIds((prevState) => prevState.filter((id) => id !== contractId));
      return;
    }

    setSelectedContractIds((prevState) => [...prevState, contractId]);
  };

  const handleUpdateContractAgreement = () => {
    if (contractData) {
      const items = contractData.items.map((contract) => ({
        contractId: contract.id,
        contextAgreement: contract.contexts,
        isAgree: !!selectedContractIds.find((contractId) => contractId === contract.id)
      }));

      updateContractAgreement({ items });
    }
  };

  const renderContrants = () =>
    contractData?.items.map((contract, i) => (
      <div key={i}>
        <div>
          <b>{contract.name}</b>
        </div>
        <div>
          <Checkbox
            onClick={() => handleContractClick(contract.id)}
            checked={!!selectedContractIds.find((id) => id === contract.id)}
          />{' '}
          <span dangerouslySetInnerHTML={{ __html: contract.testimony }} />{' '}
          {contract.user_must_accept && <b>*</b>}
        </div>
        {contract.content && (
          <div
            className={styles.consentContent}
            dangerouslySetInnerHTML={{ __html: contract.content }}
          />
        )}
      </div>
    ));

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Home')}>
      <Helmet>
        {seoData?.title ? <title>{seoData.title}</title> : <title>WiseB2B</title>}

        {seoData?.description && <meta name="description" content={seoData.description} />}

        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}

        {/* Open Graph Tags - Dla Facebook, LinkedIn, Twitter */}
        {seoData?.ogTitle && <meta property="og:title" content={seoData.ogTitle} />}
        {seoData?.ogDescription && (
          <meta property="og:description" content={seoData.ogDescription} />
        )}
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        {urlResolverData?.href_lang?.map((shortcut, i) => (
          <link
            key={i}
            rel="alternate"
            href={`${window.location.origin}/${shortcut.language}/${shortcut.url_shortcut.replace(/^\//, '')}`}
            hrefLang={shortcut.language}
          />
        ))}

        {/* canonical link */}
        <link rel="canonical" href={window.location.href} />

        {/* JSON-LD Structured Data */}
        {homePageScheme && (
          homePageScheme.map((scheme, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(scheme)}
            </script>
          ))
        )}
      </Helmet>
      <Container>
        <HtmlBlock sectionId="HOMEPAGE" />
      </Container>
      {contractData && !!contractData.items.length && isModalVisible && (
        <Modal title={t('Zgody')}>
          {renderContrants()}
          {contractData.items.some((contract) => contract.user_must_accept) && (
            <div className={styles.consentRequired}>
              <b>*</b> - <Trans>Zgoda Wymagana</Trans>
            </div>
          )}
          <div className={styles.consentActions}>
            <Button onClick={handleUpdateContractAgreement}>
              <Trans>Zapisz</Trans>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Home;
